import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Callout } from '@/shared/components/feedback/Callout/Callout';

import { OfferCodeRefusal } from '../OfferCodeRefusal';

import { useOfferCodeModalLogic, type OfferCodeFormValues } from './useOfferCodeModalLogic';

/**
 * Mapping a discount rule to a store's offer code.
 *
 * The mirroring limitation goes in the **`banner`** slot, above the fields: it
 * is a warning, and a warning read after the form is filled in is a warning that
 * arrives too late. A refusal goes under them, through `children`, because by
 * then the operator has already chosen (P23b3b).
 */
export function OfferCodeModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('subscriptions');
  const view = useOfferCodeModalLogic({
    id: typeof modalData?.['id'] === 'string' ? modalData['id'] : null,
    onDone: onCancel,
  });

  return (
    <DynamicFormModal<OfferCodeFormValues>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
      title={view.title}
      description={view.description}
      size="lg"
      isLoading={view.isLoading}
      fields={view.fields}
      schema={view.schema}
      initialValues={view.initialValues}
      submitLabel={view.submitLabel}
      cancelLabel={t('offerCodes.error.close')}
      onCancel={onCancel}
      onSubmit={view.submit}
      banner={
        <Callout tone="warning" title={view.mirroringNoticeTitle}>
          {view.mirroringNotice}
        </Callout>
      }
    >
      <OfferCodeRefusal
        message={view.refusalMessage}
        isConflict={view.refusal?.isConflict ?? false}
        isGone={view.refusal?.isGone ?? false}
        onReload={view.reload}
        onClose={onCancel}
      />
    </DynamicFormModal>
  );
}
